import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INDUSTRIES = ['FinTech', 'HealthTech', 'EdTech', 'E-commerce', 'SaaS', 'AI/ML', 'Blockchain', 'IoT', 'CleanTech', 'AgriTech'];
const STAGES = ['idea', 'pre-seed', 'seed', 'series-a'];
const INVESTOR_TYPES = ['angel', 'vc', 'family-office', 'corporate'];
const GEOGRAPHIES = ['UAE', 'Saudi Arabia', 'Qatar', 'Kuwait', 'Bahrain', 'Oman', 'Global', 'USA', 'Europe', 'Asia'];
const CURRENCIES = ['USD', 'AED', 'SAR', 'EUR', 'GBP'];
const TAGS = ['B2B', 'B2C', 'B2B2C', 'SaaS', 'Marketplace', 'Platform', 'Hardware', 'Enterprise'];
const SKILLS = ['Technical', 'Business', 'Marketing', 'Design', 'Product', 'Sales', 'Operations', 'Finance'];

// Investment Alignment Options (Founder)
const PRIMARY_INVESTOR_TYPES = ['Angel investors', 'Family offices', 'Venture capital', 'Strategic investors', 'Open to all'];
const PREVIOUS_FUNDING_OPTIONS = ['No previous funding', 'Friends & family', 'Angel round', 'Pre-seed round', 'Seed round', 'Series A+'];
const ROUND_STRUCTURE_OPTIONS = ['SAFE', 'Convertible note', 'Priced equity round', 'Revenue-based financing', 'Open to discussion'];
const VALUATION_RANGES = ['< $1M', '$1M - $5M', '$5M - $10M', '$10M - $25M', '$25M - $50M', '$50M+', 'Open to negotiation'];
const OWNERSHIP_RANGES = ['80–100%', '60–80%', '40–60%', '< 40%'];
const GROWTH_AMBITIONS = ['Sustainable profitability', 'Regional scale', 'National scale', 'Global scale', 'Hypergrowth'];
const EXIT_VISIONS = ['Strategic acquisition', 'IPO', 'Private equity buyout', 'Family business / long-term hold', 'Open to options'];

// Capital Strategy Options (Founder - new)
const LAST_ROUND_TYPES = ['None', 'Pre-seed', 'Seed', 'Series A', 'Series B+', 'Bridge'];
const CURRENT_RAISE_TYPES = ['Pre-seed', 'Seed', 'Series A', 'Series B', 'Bridge', 'Revenue-based'];
const TARGET_INVESTOR_TYPES = ['Angel', 'VC', 'Family Office', 'Corporate', 'Open'];

// Investment Preferences Options (Investor)
const CHECK_SIZE_PREFERENCES = ['< $50K', '$50K - $250K', '$250K - $1M', '$1M - $5M', '$5M+'];
const OWNERSHIP_PREFERENCES = ['< 5%', '5–10%', '10–20%', '20%+', 'Flexible'];
const GROWTH_PREFERENCES = ['Sustainable profitability', 'Regional scale', 'National scale', 'Global scale', 'Hypergrowth'];
const EXIT_HORIZONS = ['1-3 years', '3-5 years', '5-7 years', '7-10 years', '10+ years', 'Flexible'];

// Investment Strategy Options (Investor - new)
const CO_INVESTMENT_OPTIONS = ['Prefer to lead', 'Open to co-invest', 'Follow only', 'Flexible'];
const INVESTMENT_HORIZON_OPTIONS = ['1-3 years', '3-5 years', '5-7 years', '7-10 years', '10+ years'];
const VALUE_ADD_TYPES = ['Strategic', 'Financial', 'Advisory', 'Network', 'Operational'];

export default function ProfileSetup() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [existingProfile, setExistingProfile] = useState(null);

  // Theme classes
  const bg = isDark ? 'bg-slate-900' : 'bg-slate-100';
  const cardBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const textPrimary = isDark ? 'text-white' : 'text-blue-900';
  const textSecondary = isDark ? 'text-slate-300' : 'text-blue-700';
  const inputBg = isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-blue-900';
  const labelColor = isDark ? 'text-slate-200' : 'text-blue-800';
  const checkboxBg = isDark ? 'border-slate-500 data-[state=checked]:bg-blue-600' : 'border-slate-300 data-[state=checked]:bg-blue-600';

  // Founder form
  const [founderData, setFounderData] = useState({
    country: '',
    city: '',
    startup_name: '',
    industries: [],
    stage: 'idea',
    one_line_pitch: '',
    problem: '',
    solution: '',
    target_customer: '',
    traction: '',
    funding_asked_min: '',
    funding_asked_max: '',
    currency: 'USD',
    use_of_funds: '',
    team_summary: '',
    sensitive_details: '',
    tags: [],
    // Investment Alignment fields
    primary_investor_type_sought: [],
    previous_funding: '',
    amount_raised_to_date: '',
    target_round_structure: '',
    target_valuation_range: '',
    founder_ownership: '',
    growth_ambition: '',
    exit_vision: '',
    // Capital Strategy fields (new)
    last_round_type: '',
    current_raise_type: '',
    target_investor_type: ''
  });

  // Investor form
  const [investorData, setInvestorData] = useState({
    country: '',
    city: '',
    investor_type: 'angel',
    ticket_size_min: '',
    ticket_size_max: '',
    currency: 'USD',
    preferred_industries: [],
    preferred_stages: [],
    geography_focus: [],
    bio: '',
    linkedin_url: '',
    // Investment Preferences fields
    preferred_round_structure: [],
    check_size_preference: '',
    ownership_preference: '',
    growth_preference: '',
    exit_horizon: '',
    // Investment Strategy fields (new)
    preferred_sectors: [],
    co_investment_openness: '',
    investment_horizon: '',
    value_add_type: ''
  });

  // Cofounder form
  const [cofounderData, setCofounderData] = useState({
    country: '',
    city: '',
    skills: [],
    looking_for: [],
    industries: [],
    bio: '',
    linkedin_url: ''
  });

  useEffect(() => {
    if (user?.role) {
      checkExistingProfile();
    }
  }, [user?.role]);

  const checkExistingProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile/my-profile`);
      if (response.data.exists && response.data.profile) {
        const profile = response.data.profile;
        setExistingProfile(profile);
        
        // Rehydrate form data based on role
        if (user?.role === 'founder') {
          setFounderData({
            country: profile.country || '',
            city: profile.city || '',
            startup_name: profile.startup_name || '',
            industries: profile.industries || [],
            stage: profile.stage || 'idea',
            one_line_pitch: profile.one_line_pitch || '',
            problem: profile.problem || '',
            solution: profile.solution || '',
            target_customer: profile.target_customer || '',
            traction: profile.traction || '',
            funding_asked_min: profile.funding_asked_min?.toString() || '',
            funding_asked_max: profile.funding_asked_max?.toString() || '',
            currency: profile.currency || 'USD',
            use_of_funds: profile.use_of_funds || '',
            team_summary: profile.team_summary || '',
            sensitive_details: profile.sensitive_details || '',
            tags: profile.tags || [],
            // Investment Alignment fields
            primary_investor_type_sought: profile.primary_investor_type_sought || [],
            previous_funding: profile.previous_funding || '',
            amount_raised_to_date: profile.amount_raised_to_date?.toString() || '',
            target_round_structure: profile.target_round_structure || '',
            target_valuation_range: profile.target_valuation_range || '',
            founder_ownership: profile.founder_ownership || '',
            growth_ambition: profile.growth_ambition || '',
            exit_vision: profile.exit_vision || '',
            // Capital Strategy fields (new)
            last_round_type: profile.last_round_type || '',
            current_raise_type: profile.current_raise_type || '',
            target_investor_type: profile.target_investor_type || ''
          });
        } else if (user?.role === 'investor') {
          setInvestorData({
            country: profile.country || '',
            city: profile.city || '',
            investor_type: profile.investor_type || 'angel',
            ticket_size_min: profile.ticket_size_min?.toString() || '',
            ticket_size_max: profile.ticket_size_max?.toString() || '',
            currency: profile.currency || 'USD',
            preferred_industries: profile.preferred_industries || [],
            preferred_stages: profile.preferred_stages || [],
            geography_focus: profile.geography_focus || [],
            bio: profile.bio || '',
            linkedin_url: profile.linkedin_url || '',
            // Investment Preferences fields
            preferred_round_structure: profile.preferred_round_structure || [],
            check_size_preference: profile.check_size_preference || '',
            ownership_preference: profile.ownership_preference || '',
            growth_preference: profile.growth_preference || '',
            exit_horizon: profile.exit_horizon || '',
            // Investment Strategy fields (new)
            preferred_sectors: profile.preferred_sectors || [],
            co_investment_openness: profile.co_investment_openness || '',
            investment_horizon: profile.investment_horizon || '',
            value_add_type: profile.value_add_type || ''
          });
        } else if (user?.role === 'cofounder') {
          setCofounderData({
            country: profile.country || '',
            city: profile.city || '',
            skills: profile.skills || [],
            looking_for: profile.looking_for || [],
            industries: profile.industries || [],
            bio: profile.bio || '',
            linkedin_url: profile.linkedin_url || ''
          });
        }
        
        toast.info('Profile loaded. Update and save when ready.');
      }
    } catch (error) {
      console.error('Error checking profile:', error);
    }
  };

  const handleFounderSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...founderData,
        funding_asked_min: parseFloat(founderData.funding_asked_min),
        funding_asked_max: parseFloat(founderData.funding_asked_max),
        amount_raised_to_date: founderData.amount_raised_to_date ? parseFloat(founderData.amount_raised_to_date) : null
      };
      await axios.post(`${API}/profile/founder`, payload);
      toast.success(existingProfile ? 'Profile updated successfully!' : 'Profile created successfully!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInvestorSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...investorData,
        ticket_size_min: parseFloat(investorData.ticket_size_min),
        ticket_size_max: parseFloat(investorData.ticket_size_max)
      };
      await axios.post(`${API}/profile/investor`, payload);
      toast.success(existingProfile ? 'Profile updated successfully!' : 'Profile created successfully!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCofounderSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/profile/cofounder`, cofounderData);
      toast.success('Profile created successfully!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  const toggleArrayItem = (arr, item, setter) => {
    if (arr.includes(item)) {
      setter(arr.filter(i => i !== item));
    } else {
      setter([...arr, item]);
    }
  };

  if (user?.role === 'founder') {
    return (
      <div className={`min-h-screen ${bg}`}>
        <Navigation />
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className={`shadow-sm rounded-lg p-8 border ${cardBg}`}>
            <h1 className={`text-3xl font-bold mb-6 ${textPrimary}`}>Founder Profile Setup</h1>
            <form onSubmit={handleFounderSubmit} className="space-y-6" data-testid="founder-profile-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className={labelColor}>Country</Label>
                  <Input
                    value={founderData.country}
                    onChange={(e) => setFounderData({ ...founderData, country: e.target.value })}
                    className={`mt-2 ${inputBg}`}
                    required
                  />
                </div>
                <div>
                  <Label className={labelColor}>City</Label>
                  <Input
                    value={founderData.city}
                    onChange={(e) => setFounderData({ ...founderData, city: e.target.value })}
                    className={`mt-2 ${inputBg}`}
                    required
                  />
                </div>
              </div>

              <div>
                <Label className={labelColor}>Startup Name</Label>
                <Input
                  value={founderData.startup_name}
                  onChange={(e) => setFounderData({ ...founderData, startup_name: e.target.value })}
                  className={`mt-2 ${inputBg}`}
                  required
                />
              </div>

              <div>
                <Label className={`${labelColor} mb-2 block`}>Industries</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {INDUSTRIES.map(ind => (
                    <label key={ind} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={founderData.industries.includes(ind)}
                        onCheckedChange={() => toggleArrayItem(founderData.industries, ind, (arr) => setFounderData({ ...founderData, industries: arr }))}
                      />
                      <span className="text-sm text-blue-800">{ind}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className={labelColor}>Stage</Label>
                <Select value={founderData.stage} onValueChange={(val) => setFounderData({ ...founderData, stage: val })}>
                  <SelectTrigger className={`mt-2 ${inputBg}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map(stage => (
                      <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className={labelColor}>One-line Pitch</Label>
                <Input
                  value={founderData.one_line_pitch}
                  onChange={(e) => setFounderData({ ...founderData, one_line_pitch: e.target.value })}
                  className={`mt-2 ${inputBg}`}
                  required
                />
              </div>

              <div>
                <Label className={labelColor}>Problem</Label>
                <Textarea
                  value={founderData.problem}
                  onChange={(e) => setFounderData({ ...founderData, problem: e.target.value })}
                  className={`mt-2 ${inputBg}`}
                  rows={3}
                  required
                />
              </div>

              <div>
                <Label className={labelColor}>Solution</Label>
                <Textarea
                  value={founderData.solution}
                  onChange={(e) => setFounderData({ ...founderData, solution: e.target.value })}
                  className={`mt-2 ${inputBg}`}
                  rows={3}
                  required
                />
              </div>

              <div>
                <Label className={labelColor}>Target Customer</Label>
                <Textarea
                  value={founderData.target_customer}
                  onChange={(e) => setFounderData({ ...founderData, target_customer: e.target.value })}
                  className={`mt-2 ${inputBg}`}
                  rows={2}
                  required
                />
              </div>

              <div>
                <Label className={labelColor}>Traction (Optional)</Label>
                <Textarea
                  value={founderData.traction}
                  onChange={(e) => setFounderData({ ...founderData, traction: e.target.value })}
                  className={`mt-2 ${inputBg}`}
                  rows={2}
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className={labelColor}>Funding Min</Label>
                  <Input
                    type="number"
                    value={founderData.funding_asked_min}
                    onChange={(e) => setFounderData({ ...founderData, funding_asked_min: e.target.value })}
                    className={`mt-2 ${inputBg}`}
                    required
                  />
                </div>
                <div>
                  <Label className={labelColor}>Funding Max</Label>
                  <Input
                    type="number"
                    value={founderData.funding_asked_max}
                    onChange={(e) => setFounderData({ ...founderData, funding_asked_max: e.target.value })}
                    className={`mt-2 ${inputBg}`}
                    required
                  />
                </div>
                <div>
                  <Label className={labelColor}>Currency</Label>
                  <Select value={founderData.currency} onValueChange={(val) => setFounderData({ ...founderData, currency: val })}>
                    <SelectTrigger className={`mt-2 ${inputBg}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map(curr => (
                        <SelectItem key={curr} value={curr}>{curr}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className={labelColor}>Use of Funds</Label>
                <Textarea
                  value={founderData.use_of_funds}
                  onChange={(e) => setFounderData({ ...founderData, use_of_funds: e.target.value })}
                  className={`mt-2 ${inputBg}`}
                  rows={3}
                  required
                />
              </div>

              {/* Investment Alignment Section */}
              <div className={`p-6 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-blue-50 border-blue-200'}`}>
                <h2 className={`text-xl font-semibold mb-4 ${textPrimary}`}>Investment Alignment</h2>
                <p className={`text-sm mb-6 ${textSecondary}`}>Help investors understand your funding preferences and growth vision.</p>
                
                <div className="space-y-6">
                  <div>
                    <Label className={`${labelColor} mb-2 block`}>Primary Investor Type Sought</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {PRIMARY_INVESTOR_TYPES.map(type => (
                        <label key={type} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={founderData.primary_investor_type_sought.includes(type)}
                            onCheckedChange={() => toggleArrayItem(founderData.primary_investor_type_sought, type, (arr) => setFounderData({ ...founderData, primary_investor_type_sought: arr }))}
                            className={checkboxBg}
                          />
                          <span className={`text-sm ${textSecondary}`}>{type}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className={labelColor}>Previous Funding</Label>
                      <Select value={founderData.previous_funding} onValueChange={(val) => setFounderData({ ...founderData, previous_funding: val })}>
                        <SelectTrigger className={`mt-2 ${inputBg}`}>
                          <SelectValue placeholder="Select previous funding" />
                        </SelectTrigger>
                        <SelectContent>
                          {PREVIOUS_FUNDING_OPTIONS.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={labelColor}>Amount Raised to Date (Optional)</Label>
                      <Input
                        type="number"
                        value={founderData.amount_raised_to_date}
                        onChange={(e) => setFounderData({ ...founderData, amount_raised_to_date: e.target.value })}
                        className={`mt-2 ${inputBg}`}
                        placeholder="e.g., 500000"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className={labelColor}>Target Round Structure</Label>
                      <Select value={founderData.target_round_structure} onValueChange={(val) => setFounderData({ ...founderData, target_round_structure: val })}>
                        <SelectTrigger className={`mt-2 ${inputBg}`}>
                          <SelectValue placeholder="Select round structure" />
                        </SelectTrigger>
                        <SelectContent>
                          {ROUND_STRUCTURE_OPTIONS.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={labelColor}>Target Valuation Range (Optional)</Label>
                      <Select value={founderData.target_valuation_range} onValueChange={(val) => setFounderData({ ...founderData, target_valuation_range: val })}>
                        <SelectTrigger className={`mt-2 ${inputBg}`}>
                          <SelectValue placeholder="Select valuation range" />
                        </SelectTrigger>
                        <SelectContent>
                          {VALUATION_RANGES.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <Label className={labelColor}>Founder Ownership (Range)</Label>
                      <Select value={founderData.founder_ownership} onValueChange={(val) => setFounderData({ ...founderData, founder_ownership: val })}>
                        <SelectTrigger className={`mt-2 ${inputBg}`}>
                          <SelectValue placeholder="Select range" />
                        </SelectTrigger>
                        <SelectContent>
                          {OWNERSHIP_RANGES.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={labelColor}>Growth Ambition</Label>
                      <Select value={founderData.growth_ambition} onValueChange={(val) => setFounderData({ ...founderData, growth_ambition: val })}>
                        <SelectTrigger className={`mt-2 ${inputBg}`}>
                          <SelectValue placeholder="Select ambition" />
                        </SelectTrigger>
                        <SelectContent>
                          {GROWTH_AMBITIONS.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={labelColor}>Exit Vision</Label>
                      <Select value={founderData.exit_vision} onValueChange={(val) => setFounderData({ ...founderData, exit_vision: val })}>
                        <SelectTrigger className={`mt-2 ${inputBg}`}>
                          <SelectValue placeholder="Select exit vision" />
                        </SelectTrigger>
                        <SelectContent>
                          {EXIT_VISIONS.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Capital Strategy Section */}
              <div className={`p-6 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-green-50 border-green-200'}`}>
                <h2 className={`text-xl font-semibold mb-4 ${textPrimary}`}>Capital Strategy</h2>
                <p className={`text-sm mb-6 ${textSecondary}`}>Define your current capital raise to help investors understand your needs.</p>
                
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <Label className={labelColor}>Last Round Type</Label>
                    <Select value={founderData.last_round_type} onValueChange={(val) => setFounderData({ ...founderData, last_round_type: val })}>
                      <SelectTrigger className={`mt-2 ${inputBg}`}>
                        <SelectValue placeholder="Select last round" />
                      </SelectTrigger>
                      <SelectContent>
                        {LAST_ROUND_TYPES.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={labelColor}>Current Raise Type</Label>
                    <Select value={founderData.current_raise_type} onValueChange={(val) => setFounderData({ ...founderData, current_raise_type: val })}>
                      <SelectTrigger className={`mt-2 ${inputBg}`}>
                        <SelectValue placeholder="Select current raise" />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENT_RAISE_TYPES.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={labelColor}>Target Investor Type</Label>
                    <Select value={founderData.target_investor_type} onValueChange={(val) => setFounderData({ ...founderData, target_investor_type: val })}>
                      <SelectTrigger className={`mt-2 ${inputBg}`}>
                        <SelectValue placeholder="Select investor type" />
                      </SelectTrigger>
                      <SelectContent>
                        {TARGET_INVESTOR_TYPES.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <Label className={labelColor}>Team Summary</Label>
                <Textarea
                  value={founderData.team_summary}
                  onChange={(e) => setFounderData({ ...founderData, team_summary: e.target.value })}
                  className={`mt-2 ${inputBg}`}
                  rows={3}
                  required
                />
              </div>

              <div>
                <Label className={labelColor}>Sensitive Details (NDA-protected)</Label>
                <Textarea
                  value={founderData.sensitive_details}
                  onChange={(e) => setFounderData({ ...founderData, sensitive_details: e.target.value })}
                  className={`mt-2 ${inputBg}`}
                  rows={4}
                  placeholder="Patents, competitive advantages, financial details, etc."
                  required
                />
              </div>

              <div>
                <Label className={`${labelColor} mb-2 block`}>Tags</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {TAGS.map(tag => (
                    <label key={tag} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={founderData.tags.includes(tag)}
                        onCheckedChange={() => toggleArrayItem(founderData.tags, tag, (arr) => setFounderData({ ...founderData, tags: arr }))}
                      />
                      <span className="text-sm text-blue-800">{tag}</span>
                    </label>
                  ))}
                </div>
              </div>

              <Button type="submit" className="btn-primary w-full py-6" disabled={loading} data-testid="submit-profile">
                {loading ? 'Saving...' : 'Save Profile'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (user?.role === 'investor') {
    return (
      <div className={`min-h-screen ${bg}`}>
        <Navigation />
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className={`shadow-sm rounded-lg p-8 border ${cardBg}`}>
            <h1 className={`text-3xl font-bold mb-6 ${textPrimary}`}>Investor Profile Setup</h1>
            <form onSubmit={handleInvestorSubmit} className="space-y-6" data-testid="investor-profile-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className={labelColor}>Country</Label>
                  <Input
                    value={investorData.country}
                    onChange={(e) => setInvestorData({ ...investorData, country: e.target.value })}
                    className={`mt-2 ${inputBg}`}
                    required
                  />
                </div>
                <div>
                  <Label className={labelColor}>City</Label>
                  <Input
                    value={investorData.city}
                    onChange={(e) => setInvestorData({ ...investorData, city: e.target.value })}
                    className={`mt-2 ${inputBg}`}
                    required
                  />
                </div>
              </div>

              <div>
                <Label className={labelColor}>Investor Type</Label>
                <Select value={investorData.investor_type} onValueChange={(val) => setInvestorData({ ...investorData, investor_type: val })}>
                  <SelectTrigger className={`mt-2 ${inputBg}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INVESTOR_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className={labelColor}>Ticket Size Min</Label>
                  <Input
                    type="number"
                    value={investorData.ticket_size_min}
                    onChange={(e) => setInvestorData({ ...investorData, ticket_size_min: e.target.value })}
                    className={`mt-2 ${inputBg}`}
                    required
                  />
                </div>
                <div>
                  <Label className={labelColor}>Ticket Size Max</Label>
                  <Input
                    type="number"
                    value={investorData.ticket_size_max}
                    onChange={(e) => setInvestorData({ ...investorData, ticket_size_max: e.target.value })}
                    className={`mt-2 ${inputBg}`}
                    required
                  />
                </div>
                <div>
                  <Label className={labelColor}>Currency</Label>
                  <Select value={investorData.currency} onValueChange={(val) => setInvestorData({ ...investorData, currency: val })}>
                    <SelectTrigger className={`mt-2 ${inputBg}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map(curr => (
                        <SelectItem key={curr} value={curr}>{curr}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className={`${labelColor} mb-2 block`}>Preferred Industries</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {INDUSTRIES.map(ind => (
                    <label key={ind} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={investorData.preferred_industries.includes(ind)}
                        onCheckedChange={() => toggleArrayItem(investorData.preferred_industries, ind, (arr) => setInvestorData({ ...investorData, preferred_industries: arr }))}
                      />
                      <span className="text-sm text-blue-800">{ind}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className={`${labelColor} mb-2 block`}>Preferred Stages</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {STAGES.map(stage => (
                    <label key={stage} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={investorData.preferred_stages.includes(stage)}
                        onCheckedChange={() => toggleArrayItem(investorData.preferred_stages, stage, (arr) => setInvestorData({ ...investorData, preferred_stages: arr }))}
                      />
                      <span className="text-sm text-blue-800">{stage}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className={`${labelColor} mb-2 block`}>Geography Focus</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {GEOGRAPHIES.map(geo => (
                    <label key={geo} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={investorData.geography_focus.includes(geo)}
                        onCheckedChange={() => toggleArrayItem(investorData.geography_focus, geo, (arr) => setInvestorData({ ...investorData, geography_focus: arr }))}
                      />
                      <span className="text-sm text-blue-800">{geo}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Investment Preferences Section */}
              <div className={`p-6 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-blue-50 border-blue-200'}`}>
                <h2 className={`text-xl font-semibold mb-4 ${textPrimary}`}>Investment Preferences</h2>
                <p className={`text-sm mb-6 ${textSecondary}`}>Define your investment style and preferences to help founders understand your approach.</p>
                
                <div className="space-y-6">
                  <div>
                    <Label className={`${labelColor} mb-2 block`}>Preferred Round Structure</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {ROUND_STRUCTURE_OPTIONS.map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={investorData.preferred_round_structure.includes(opt)}
                            onCheckedChange={() => toggleArrayItem(investorData.preferred_round_structure, opt, (arr) => setInvestorData({ ...investorData, preferred_round_structure: arr }))}
                            className={checkboxBg}
                          />
                          <span className={`text-sm ${textSecondary}`}>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className={labelColor}>Check Size Preference</Label>
                      <Select value={investorData.check_size_preference} onValueChange={(val) => setInvestorData({ ...investorData, check_size_preference: val })}>
                        <SelectTrigger className={`mt-2 ${inputBg}`}>
                          <SelectValue placeholder="Select check size" />
                        </SelectTrigger>
                        <SelectContent>
                          {CHECK_SIZE_PREFERENCES.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={labelColor}>Ownership Preference</Label>
                      <Select value={investorData.ownership_preference} onValueChange={(val) => setInvestorData({ ...investorData, ownership_preference: val })}>
                        <SelectTrigger className={`mt-2 ${inputBg}`}>
                          <SelectValue placeholder="Select ownership" />
                        </SelectTrigger>
                        <SelectContent>
                          {OWNERSHIP_PREFERENCES.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className={labelColor}>Growth Preference</Label>
                      <Select value={investorData.growth_preference} onValueChange={(val) => setInvestorData({ ...investorData, growth_preference: val })}>
                        <SelectTrigger className={`mt-2 ${inputBg}`}>
                          <SelectValue placeholder="Select growth preference" />
                        </SelectTrigger>
                        <SelectContent>
                          {GROWTH_PREFERENCES.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={labelColor}>Exit Horizon</Label>
                      <Select value={investorData.exit_horizon} onValueChange={(val) => setInvestorData({ ...investorData, exit_horizon: val })}>
                        <SelectTrigger className={`mt-2 ${inputBg}`}>
                          <SelectValue placeholder="Select exit horizon" />
                        </SelectTrigger>
                        <SelectContent>
                          {EXIT_HORIZONS.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Investment Strategy Section */}
              <div className={`p-6 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-green-50 border-green-200'}`}>
                <h2 className={`text-xl font-semibold mb-4 ${textPrimary}`}>Investment Strategy</h2>
                <p className={`text-sm mb-6 ${textSecondary}`}>Define your investment approach to help founders understand your style.</p>
                
                <div className="space-y-6">
                  <div>
                    <Label className={`${labelColor} mb-2 block`}>Preferred Sectors</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {INDUSTRIES.map(sector => (
                        <label key={sector} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={investorData.preferred_sectors.includes(sector)}
                            onCheckedChange={() => toggleArrayItem(investorData.preferred_sectors, sector, (arr) => setInvestorData({ ...investorData, preferred_sectors: arr }))}
                            className={checkboxBg}
                          />
                          <span className={`text-sm ${textSecondary}`}>{sector}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <Label className={labelColor}>Co-Investment Openness</Label>
                      <Select value={investorData.co_investment_openness} onValueChange={(val) => setInvestorData({ ...investorData, co_investment_openness: val })}>
                        <SelectTrigger className={`mt-2 ${inputBg}`}>
                          <SelectValue placeholder="Select preference" />
                        </SelectTrigger>
                        <SelectContent>
                          {CO_INVESTMENT_OPTIONS.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={labelColor}>Investment Horizon</Label>
                      <Select value={investorData.investment_horizon} onValueChange={(val) => setInvestorData({ ...investorData, investment_horizon: val })}>
                        <SelectTrigger className={`mt-2 ${inputBg}`}>
                          <SelectValue placeholder="Select horizon" />
                        </SelectTrigger>
                        <SelectContent>
                          {INVESTMENT_HORIZON_OPTIONS.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={labelColor}>Value-Add Type</Label>
                      <Select value={investorData.value_add_type} onValueChange={(val) => setInvestorData({ ...investorData, value_add_type: val })}>
                        <SelectTrigger className={`mt-2 ${inputBg}`}>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {VALUE_ADD_TYPES.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label className={labelColor}>Bio</Label>
                <Textarea
                  value={investorData.bio}
                  onChange={(e) => setInvestorData({ ...investorData, bio: e.target.value })}
                  className={`mt-2 ${inputBg}`}
                  rows={4}
                  required
                />
              </div>

              <div>
                <Label className={labelColor}>LinkedIn URL (Optional)</Label>
                <Input
                  value={investorData.linkedin_url}
                  onChange={(e) => setInvestorData({ ...investorData, linkedin_url: e.target.value })}
                  className={`mt-2 ${inputBg}`}
                  placeholder="https://linkedin.com/in/..."
                />
              </div>

              <Button type="submit" className="btn-primary w-full py-6" disabled={loading} data-testid="submit-profile">
                {loading ? 'Saving...' : 'Save Profile'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (user?.role === 'cofounder') {
    return (
      <div className={`min-h-screen ${bg}`}>
        <Navigation />
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className={`shadow-sm rounded-lg p-8 border ${cardBg}`}>
            <h1 className={`text-3xl font-bold mb-6 ${textPrimary}`}>Co-founder Profile Setup</h1>
            <form onSubmit={handleCofounderSubmit} className="space-y-6" data-testid="cofounder-profile-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className={labelColor}>Country</Label>
                  <Input
                    value={cofounderData.country}
                    onChange={(e) => setCofounderData({ ...cofounderData, country: e.target.value })}
                    className={`mt-2 ${inputBg}`}
                    required
                  />
                </div>
                <div>
                  <Label className={labelColor}>City</Label>
                  <Input
                    value={cofounderData.city}
                    onChange={(e) => setCofounderData({ ...cofounderData, city: e.target.value })}
                    className={`mt-2 ${inputBg}`}
                    required
                  />
                </div>
              </div>

              <div>
                <Label className={`${labelColor} mb-2 block`}>My Skills</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {SKILLS.map(skill => (
                    <label key={skill} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={cofounderData.skills.includes(skill)}
                        onCheckedChange={() => toggleArrayItem(cofounderData.skills, skill, (arr) => setCofounderData({ ...cofounderData, skills: arr }))}
                      />
                      <span className="text-sm text-blue-800">{skill}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className={`${labelColor} mb-2 block`}>Looking For</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {SKILLS.map(skill => (
                    <label key={skill} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={cofounderData.looking_for.includes(skill)}
                        onCheckedChange={() => toggleArrayItem(cofounderData.looking_for, skill, (arr) => setCofounderData({ ...cofounderData, looking_for: arr }))}
                      />
                      <span className="text-sm text-blue-800">{skill}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className={`${labelColor} mb-2 block`}>Interested Industries</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {INDUSTRIES.map(ind => (
                    <label key={ind} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={cofounderData.industries.includes(ind)}
                        onCheckedChange={() => toggleArrayItem(cofounderData.industries, ind, (arr) => setCofounderData({ ...cofounderData, industries: arr }))}
                      />
                      <span className="text-sm text-blue-800">{ind}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className={labelColor}>Bio</Label>
                <Textarea
                  value={cofounderData.bio}
                  onChange={(e) => setCofounderData({ ...cofounderData, bio: e.target.value })}
                  className={`mt-2 ${inputBg}`}
                  rows={4}
                  required
                />
              </div>

              <div>
                <Label className={labelColor}>LinkedIn URL (Optional)</Label>
                <Input
                  value={cofounderData.linkedin_url}
                  onChange={(e) => setCofounderData({ ...cofounderData, linkedin_url: e.target.value })}
                  className={`mt-2 ${inputBg}`}
                  placeholder="https://linkedin.com/in/..."
                />
              </div>

              <Button type="submit" className="btn-primary w-full py-6" disabled={loading} data-testid="submit-profile">
                {loading ? 'Saving...' : 'Save Profile'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
